import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { BerService } from '../ber.service';
import { naploNyomtat, NyomtatasOszlop } from '../../naplo/naplo-nyomtatas';

/**
 *  BÉR – JUTTATÁSOK ÉS EFO: a kiszolgáló (backend/ber-juttatas.js) hívásai és a négy képernyő
 *  (Adatok rögzítése (EFO) · Cégtelefon · Reprezentáció · Béren kívüli juttatások listája) közös
 *  segédei: számformázás, CSV-letöltés, nyomtatás a NAPLO nyomtatási lapjával.
 */
@Injectable({ providedIn: 'root' })
export class BerJuttatasApi {
  private readonly base = '/api/ber-juttatas';
  constructor(private http: HttpClient, private ber: BerService) {}

  get<T = any>(p: string): Promise<T> { return firstValueFrom(this.http.get<T>(this.base + p)); }
  post<T = any>(p: string, b: any): Promise<T> { return firstValueFrom(this.http.post<T>(this.base + p, b)); }
  put<T = any>(p: string, b: any): Promise<T> { return firstValueFrom(this.http.put<T>(this.base + p, b)); }
  del<T = any>(p: string): Promise<T> { return firstValueFrom(this.http.delete<T>(this.base + p)); }

  /** Az aktív ügyfél adatai a nyomtatás fejlécéhez (név, adószám, cím) – hiba esetén csak a név. */
  async nyomtatasiUgyfel(): Promise<{ nev: string | null; adoszam?: string | null; cim?: string | null }> {
    const a = this.ber.aktiv;
    try {
      const lista: any[] = await this.ber.get('/ugyfelek/' + a.intezmeny);
      const u = (lista || []).find((x) => x.id === a.ugyfelId);
      const cim = [u?.irsz, u?.telepules].filter(Boolean).join(' ') + (u?.cim ? ', ' + u.cim : '');
      return { nev: a.ugyfelNev, adoszam: u?.adoszam || null, cim: cim.replace(/^, /, '') || null };
    } catch { return { nev: a.ugyfelNev }; }
  }

  /** Nyomtatás a NAPLO nyomtatási lapjával (fekvő A4, ismétlődő fejléc, összesítő sor). */
  async nyomtat(cim: string, szurok: [string, string][], oszlopok: NyomtatasOszlop[], sorok: any[],
                ertek: (r: any, o: NyomtatasOszlop) => string, osszeg?: (o: NyomtatasOszlop, i: number) => string) {
    naploNyomtat({
      cim, ugyfel: await this.nyomtatasiUgyfel(), ev: this.ber.aktiv.ev, mod: 'Bérszámfejtés',
      szurok, oszlopok, sorok, ertek, osszegSor: osszeg, uresOszlopNelkul: false,
    });
  }
}

/** Ezres tagolás MINDIG (a magyar formátum 4 jegyűnél elhagyná), egész forintra. */
export function ftSzoveg(v: any): string {
  const n = Math.round(Number(v) || 0);
  return (n < 0 ? '−' : '') + String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}
/** Tizedes tört magyar formában (1.18 → „1,18”), legfeljebb három tizedesre. */
export function tizedes(v: any): string { return String(Math.round((Number(v) || 0) * 1000) / 1000).replace('.', ','); }
/** A törtként tárolt kulcs százalékban (0,15 → „15”, 0,0075 → „0,75”, 0,01125 → „1,125”). */
export function szazalek(v: any): string { return tizedes((Number(v) || 0) * 100); }

/** CSV letöltése pontosvesszővel, BOM-mal (az Excel így ismeri fel az ékezeteket). */
export function csvLetolt(fajlnev: string, sorok: (string | number | null | undefined)[][]) {
  const csv = sorok.map((sor) => sor.map((c) => {
    const s = c === null || c === undefined ? '' : String(c);
    return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(';')).join('\r\n');
  const blob = new Blob(['﻿' + csv + '\r\n'], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = fajlnev;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

/** A hónapválasztó „Egész év" lehetőséggel (0). A közös aktív hónapot csak 1–12 írja. */
export const HONAPOK_EVVEL = [{ value: 0, label: 'Egész év' }].concat([
  'Január', 'Február', 'Március', 'Április', 'Május', 'Június', 'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December',
].map((label, i) => ({ value: i + 1, label })));
